import React, { useState, useRef } from "react";
import { useDrag, useDrop } from "react-dnd";
import { IconPhone, IconClock, IconCrown } from "@tabler/icons-react";
import { useCRM } from "../CRMContext";
import { formatPhoneNumber } from "../../../../helpers/Helpers";

const ItemTypes = { CONTACT: "CONTACT" };

const formatCurrency = (value) => {
  if (!value && value !== 0) return null;
  return new Intl.NumberFormat("pt-BR", { style: "currency", currency: "BRL" }).format(value);
};

const getInitials = (name) => {
  if (!name) return "?";
  return name
    .split(" ")
    .map((n) => n[0])
    .slice(0, 2)
    .join("")
    .toUpperCase();
};

const ContactCard = ({ contact, index, columnId, columnColor }) => {
  const { openDrawer, toggleSelectContact, state, moveContactLocal, moveContactAPI } = useCRM();
  const [hovered, setHovered] = useState(false);
  const ref = useRef(null);
  const isSelected = state.selectedContacts.includes(contact.id);

  const stagnantClass = contact.is_stagnant
    ? contact.days_in_stage > (contact.max_days || 999) * 2
      ? "crm-card--stagnant-critical"
      : "crm-card--stagnant"
    : "";

  const daysClass = contact.is_stagnant
    ? contact.days_in_stage > (contact.max_days || 999) * 2
      ? "crm-card__days--critical"
      : "crm-card__days--stagnant"
    : "";

  const [{ isDragging }, drag] = useDrag({
    type: ItemTypes.CONTACT,
    item: { type: ItemTypes.CONTACT, contactId: contact.id, sourceColumnId: columnId, index },
    collect: (monitor) => ({
      isDragging: monitor.isDragging(),
    }),
    end: (item, monitor) => {
      if (monitor.didDrop()) {
        // API call is handled by the drop target (Column or ContactCard)
        return;
      }
      // If not dropped on a valid target, no action needed (state wasn't changed)
    },
  });

  const [, drop] = useDrop({
    accept: ItemTypes.CONTACT,
    hover: (item, monitor) => {
      if (!ref.current) return;
      const dragIndex = item.index;
      const hoverIndex = index;
      const dragColumnId = item.sourceColumnId;
      const hoverColumnId = columnId;

      if (dragIndex === hoverIndex && dragColumnId === hoverColumnId) return;

      const hoverBoundingRect = ref.current.getBoundingClientRect();
      const hoverMiddleY = (hoverBoundingRect.bottom - hoverBoundingRect.top) / 2;
      const clientOffset = monitor.getClientOffset();
      const hoverClientY = clientOffset.y - hoverBoundingRect.top;

      // Dragging down: only move when past the midpoint
      if (dragColumnId === hoverColumnId && dragIndex < hoverIndex && hoverClientY < hoverMiddleY) return;
      // Dragging up: only move when past the midpoint
      if (dragColumnId === hoverColumnId && dragIndex > hoverIndex && hoverClientY > hoverMiddleY) return;

      moveContactLocal(item.contactId, dragColumnId, hoverColumnId, hoverIndex);

      item.index = hoverIndex;
      item.sourceColumnId = hoverColumnId;
    },
    drop: (item) => {
      // Persist to API on drop
      moveContactAPI(item.contactId, item.sourceColumnId, item.index);
      return { dropped: true };
    },
  });

  drag(drop(ref));

  return (
    <div
      ref={ref}
      className={`crm-card ${isSelected ? "crm-card--selected" : ""} ${isDragging ? "crm-card--dragging" : ""} ${stagnantClass}`}
      style={{ opacity: isDragging ? 0.4 : 1, borderLeft: `3px solid ${columnColor || "#3b82f6"}` }}
      onClick={() => openDrawer(contact.id)}
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
    >
      {/* Checkbox */}
      <div
        className="crm-card__checkbox"
        style={{ opacity: hovered || isSelected ? 1 : 0 }}
        onClick={(e) => e.stopPropagation()}
      >
        <input
          type="checkbox"
          checked={isSelected}
          onChange={() => toggleSelectContact(contact.id)}
        />
      </div>

      <h4 className="crm-card__name" style={{ paddingLeft: hovered || isSelected ? 24 : 0, display: "inline-flex", alignItems: "center", gap: 6 }}>
        <span>{contact.name || contact.phone}</span>
        {(contact.is_converted || contact.customer) && (
          <span
            title="Convertido em cliente"
            style={{
              display: "inline-flex", alignItems: "center", gap: 2,
              padding: "1px 6px", borderRadius: 999,
              background: "#dcfce7", color: "#166534",
              fontSize: 10, fontWeight: 600,
            }}
          >
            <IconCrown size={10} /> Cliente
          </span>
        )}
      </h4>

      {contact.phone && (
        <div className="crm-card__phone">
          <IconPhone size={12} />
          <span>{formatPhoneNumber(contact.phone) || contact.phone}</span>
        </div>
      )}

      {contact.deal_value > 0 && (
        <div className="crm-card__value">{formatCurrency(contact.deal_value)}</div>
      )}

      {/* Priority & Temperature badges */}
      {(contact.priority || contact.temperature) && (
        <div style={{ display: "flex", gap: 6, flexWrap: "wrap", marginTop: 4, marginBottom: 4 }}>
          {contact.priority && (
            <span className={`crm-badge crm-badge--${contact.priority === "high" ? "danger" : contact.priority === "medium" ? "warning" : "info"}`}>
              {contact.priority === "high" ? "Alta" : contact.priority === "medium" ? "Média" : "Baixa"}
            </span>
          )}
          {contact.temperature && (
            <span className={`crm-badge crm-badge--${contact.temperature === "hot" ? "danger" : contact.temperature === "warm" ? "warning" : "info"}`}>
              {contact.temperature === "hot" ? "Quente" : contact.temperature === "warm" ? "Morno" : "Frio"}
            </span>
          )}
        </div>
      )}

      {/* Tags */}
      {contact.contact_tags_data && contact.contact_tags_data.length > 0 && (
        <div className="crm-card__tags">
          {contact.contact_tags_data.slice(0, 3).map((tag) => (
            <span key={tag.id} className="crm-card__tag" style={{ background: tag.color || "#64748b" }}>
              {tag.name}
            </span>
          ))}
          {contact.contact_tags_data.length > 3 && (
            <span className="crm-card__tag" style={{ background: "#94a3b8" }}>
              +{contact.contact_tags_data.length - 3}
            </span>
          )}
        </div>
      )}

      {/* Label (legacy) */}
      {contact.label && contact.label.name && (
        <div className="crm-card__tags">
          <span className="crm-card__tag" style={{ background: contact.label.color || "#64748b" }}>
            {contact.label.name}
          </span>
        </div>
      )}

      {/* Footer */}
      <div className="crm-card__footer">
        <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
          {contact.owner_name && (
            <span className="crm-card__owner-avatar" title={contact.owner_name}>
              {getInitials(contact.owner_name)}
            </span>
          )}
          {contact.days_in_stage != null && (
            <span className={`crm-card__days ${daysClass}`}>
              <IconClock size={12} style={{ marginRight: 2 }} />
              {contact.days_in_stage}d
            </span>
          )}
        </div>

        {contact.pending_activities > 0 && (
          <span className="crm-card__activity-badge">
            {contact.pending_activities} ativ.
          </span>
        )}
      </div>
    </div>
  );
};

export default ContactCard;
